
import { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, CheckSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { findPotentialDuplicates, suggestProviderToKeep, getProviderIdsToDelete } from '@/utils/peoProviderUtils';
import { usePEOProviders } from './hooks/usePEOProviders';
import type { PEOProvider } from '@/types/peo.types';

const DuplicateProvidersAlert = () => {
  const [duplicateGroups, setDuplicateGroups] = useState<{ group: string; items: PEOProvider[] }[]>([]);
  const [processing, setProcessing] = useState<{[key: string]: boolean}>({});
  
  const { 
    providers, 
    isLoading, 
    handleProviderDelete, 
    isDeletingProvider 
  } = usePEOProviders();
  
  useEffect(() => {
    if (providers.length > 0) {
      const duplicates = findPotentialDuplicates(providers);
      setDuplicateGroups(duplicates);
    }
  }, [providers]);

  const handleKeepProvider = async (groupIndex: number, providerToKeep: PEOProvider) => {
    try {
      setProcessing({...processing, [groupIndex]: true});
      
      // Get all provider IDs to delete (all except the one to keep)
      const providersToDelete = getProviderIdsToDelete(
        duplicateGroups[groupIndex].items,
        providerToKeep.id
      );
      
      console.log(`Keeping provider "${providerToKeep.name}" and removing ${providersToDelete.length} duplicates:`, 
        providersToDelete);
      
      // Delete the duplicate providers one by one
      let allSuccessful = true;
      for (const id of providersToDelete) {
        const success = await handleProviderDelete(id);
        if (!success) {
          allSuccessful = false;
        }
      }
      
      if (allSuccessful) {
        // Update state after successful deletion
        const updatedGroups = [...duplicateGroups];
        updatedGroups.splice(groupIndex, 1);
        setDuplicateGroups(updatedGroups);
        console.log('Duplicate group removed from UI after successful deletion');
      } else {
        console.error('Failed to delete some duplicate providers');
      }
      
    } catch (error) {
      console.error("Error resolving duplicates:", error);
    } finally {
      setProcessing({...processing, [groupIndex]: false});
    }
  };
  
  if (isLoading) {
    return <div className="flex items-center space-x-2">
      <div className="animate-spin h-4 w-4 border-2 border-primary rounded-full border-t-transparent"></div>
      <span>Analyzing providers...</span>
    </div>;
  }
  
  if (duplicateGroups.length === 0) {
    return (
      <Alert className="bg-primary/10 border-primary/20">
        <CheckCircle className="h-4 w-4 text-primary" />
        <AlertTitle>No duplicates found</AlertTitle>
        <AlertDescription>
          All PEO provider names appear to be unique.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-6">
      <Alert className="bg-primary/10 border-primary/20">
        <AlertTriangle className="h-4 w-4 text-primary" />
        <AlertTitle>Potential duplicates detected</AlertTitle>
        <AlertDescription>
          We found {duplicateGroups.length} groups of providers with similar names.
          Select which record to keep for each group.
        </AlertDescription>
      </Alert>
      
      <div className="space-y-6">
        {duplicateGroups.map((group, index) => {
          const suggested = suggestProviderToKeep(group.items);
          
          return (
            <Card key={index} className="border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium">Duplicate Group #{index + 1}</CardTitle>
                <CardDescription>
                  Select one provider to keep and delete the others:
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {group.items.map(provider => (
                  <div key={provider.id} className="flex items-start p-3 rounded-md border border-gray-200">
                    <div className="flex-1">
                      <div className="font-medium flex gap-2 items-center">
                        {provider.name}
                        {provider.id === suggested.id && (
                          <Badge className="bg-primary text-white hover:bg-primary/90" variant="default">
                            Suggested
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        <span className="inline-block mr-4">ID: {provider.id.slice(0, 8)}...</span>
                        <span className="inline-block mr-4">Years: {provider.years_in_business}</span>
                        <span className="inline-block">ESAC: {provider.is_esac_accredited ? 'Yes' : 'No'}</span>
                      </div>
                      {provider.specialties && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {provider.specialties.slice(0, 3).map((specialty, idx) => (
                            <span 
                              key={idx} 
                              className="text-xs px-2 py-1 rounded-full bg-primary text-white"
                            >
                              {specialty}
                            </span>
                          ))}
                          {provider.specialties.length > 3 && (
                            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                              +{provider.specialties.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="ml-2"
                      onClick={() => handleKeepProvider(index, provider)}
                      disabled={processing[index] || isDeletingProvider}
                    >
                      <CheckSquare className="mr-1 h-4 w-4" />
                      Keep This One
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DuplicateProvidersAlert;
